import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type {
  Asset_Status_Enum,
  UpdateAssetMutation,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  namedOperations,
  UpdateAssetDocument,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

export interface UpdateAssetInput {
  Id: string;
  Title: string;
  Status: Asset_Status_Enum;
  Description?: null | string;
  AssetType?: null | string;
  SerialNumber?: null | string;
  PurchaseDate?: null | string;
  Location?: null | string;
  Cost?: null | number;
  DepreciationMethod?: null | string;
  Assignee?: null | string;
  OriginalTimestamp: string;
  CustomAttributeData?: CustomAttributeData | null;
  OwnerUserIds: string[];
  OwnerGroupIds: string[];
  ContributorUserIds: string[];
  ContributorGroupIds: string[];
  TagTypeIds: string[];
  DepartmentTypeIds: string[];
}

/**
 * Call options accepted for signature compatibility with governed update
 * callers — asset updates are not change-request gated, so they are unused
 * on both branches (matching the legacy wrapper).
 */
interface UpdateAssetOptions {
  confirmChangeRequest?: boolean;
  hasFileChanges?: boolean;
}

export const useUpdateAsset = createMutationHook({
  name: 'updateAsset',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.asset.update.mutationOptions({ onSuccess }),
  mapInput: (input: UpdateAssetInput) => input,
  invalidate: (trpc) => [
    trpc.frontend.asset.register.queryKey(),
    trpc.frontend.asset.assetById.queryKey(),
    // COI-889: this hook rewrites the owner/contributor sets, whose triggers
    // maintain the grant cache `perm_v2_entity_permissions` reads (60-min
    // window, `refetchOnMount: false`, so it never self-heals).
    trpc.frontend.permission.getEntityPermissions.queryKey(),
  ],
  mapResponse: (): UpdateAssetMutation => ({
    updateChildAsset: {
      __typename: 'GenericMutationOutput',
      affected_rows: 1,
    },
  }),
  graphqlDocument: UpdateAssetDocument,
  graphqlVariables: (
    input: UpdateAssetInput,
    _options?: UpdateAssetOptions
  ) => ({
    ...input,
  }),
  evictFields: ['asset', 'asset_aggregate'],
  refetchQueries: [namedOperations.Query.getAssetById],
});
